import { Col, Form, InputNumber, Row } from "antd";
import { FC, useEffect, useState } from "react";
import InputPrice from "../../../components/form/inputPrice";
import DividerLine from "../../../components/lineSplit";
import LeasingStore from "../../../contexts/leasingStore";
import _t from "../../../lang/translate";
import format from "../../../utilities/formatNumbers";
import getLimits from "../../../utilities/getLimits";
import { isPayoutInLimit, isRestValueInLimit } from "./calc/leasingCalc";
import { LeasingDealType } from "./calc/leasingCalcTypes";

interface LeasingLimitsFieldsProps {
  inLimitStatus: boolean;
  onInLimitStatusChange: (s: boolean) => void;
}

const LeasingLimitsFields: FC<LeasingLimitsFieldsProps> = ({ onInLimitStatusChange, inLimitStatus }) => {
  const { leasingCase } = LeasingStore.useContainer();
  const { flags } = leasingCase;
  const { readonly, inputLocked } = flags;
  const disabled = readonly || inputLocked;
  const [restValuePercentage, setRestValuePercentage] = useState<number | null>(null)
  const [payoutPercentage, setPayoutPercentage] = useState<number | null>(null)

  useEffect(() => {
    setPayoutPercentage(calculatePercentage(leasingCase.deal?.input?.wishedPayout, leasingCase.deal?.output?.totalCost))
    const restValue = leasingCase.deal.input.wishedRestValue || 0;
    const vehiclePrice = leasingCase.deal.input.effectiveVehiclePrice || 0;
    if (restValue && vehiclePrice) {
      setRestValuePercentage(calculateRestValuePercentage(restValue, vehiclePrice))
    }

  }, [leasingCase.deal])


  return (
    <Form.Item noStyle shouldUpdate>
      {({ getFieldsValue, setFieldsValue }) => {
        const deal: LeasingDealType = getFieldsValue(true);
        const { wishedPayoutMin, wishedPayoutMax, wishedRestValueMin, wishedRestValueMax } = deal?.output.limits || {};

        const vehiclePrice = deal.input?.effectiveVehiclePrice ?? 0;
        const totalCost = deal.output.totalCost;
        const totalPayout = (deal.input.wishedDeposit || 0) + (deal.input.wishedPayout || 0);
        const isUnlimitedValues = deal?.input.isUnlimitedValues;
        const payoutInLimit = isPayoutInLimit(deal);
        const restValueInLimit = isRestValueInLimit(deal);
        const inLimit = payoutInLimit && restValueInLimit;

        return (
          <>
            <InLimitStatusSync inLimit={inLimit} inLimitStatus={inLimitStatus} onChange={onInLimitStatusChange} />
            <Form.Item name={["input", "wishedDeposit"]} label={_t("deposit")}>
              <InputPrice disabled={disabled} />
            </Form.Item>
            <Form.Item label={_t("wished", "first_time_payment")}>
              <Row gutter={8} wrap={false}>
                <Col flex={1}>
                  <Form.Item name={["input", "wishedPayout"]}>
                    <InputPrice disabled={disabled} suffix={"kr."}
                      onChange={(wishedPayout) => {
                        setPayoutPercentage(calculatePercentage(wishedPayout as number, totalCost))
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col flex={0}>
                  <Form.Item noStyle name={"__IGNORE_THIS_FIELD__custom_ui_name_to_trigger_onValuesChange_wishedPayout"}
                    getValueProps={() => ({})}
                  >
                    <InputNumber
                      disabled={disabled}
                      min={0}
                      addonAfter="%"
                      value={payoutPercentage}
                      onChange={(val) => {
                        setFieldsValue({ input: { wishedPayout: calculatePercentagePayout(val, totalCost) } })
                        setPayoutPercentage(val)
                      }}
                      style={!restValueInLimit ? { borderColor: "red", color: "red", width: 96 } : { width: 96 }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>

            {!isUnlimitedValues && (
              <Form.Item label={_t("self_financing")}>
                <Row justify="space-between" className={!payoutInLimit ? "text-danger" : ""}>
                  <Col className="text-bold">{format.price(totalPayout)}</Col>
                  <Col>{getLimits(wishedPayoutMin, wishedPayoutMax)}</Col>
                </Row>
              </Form.Item>
            )}
            <DividerLine />
            <Form.Item label={_t("wished", "restvalue")}>
              <Row gutter={8} wrap={false}>
                <Col flex={1}>
                  <Form.Item noStyle name={["input", "wishedRestValue"]}>
                    <InputPrice
                      disabled={disabled}
                      min={0}
                      suffix={getLimits(wishedRestValueMin, wishedRestValueMax)}
                      style={!restValueInLimit ? { borderColor: "red", color: "red" } : {}}
                      onChange={(wishedRestValue) => {
                        setRestValuePercentage(calculateRestValuePercentage(wishedRestValue as number, vehiclePrice))
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col flex={0}>
                  <Form.Item noStyle name={"__IGNORE_THIS_FIELD__custom_ui_name_to_trigger_onValuesChange_restValue"}
                    getValueProps={() => ({})}
                  >
                    <InputNumber
                      disabled={disabled}
                      min={0}
                      addonAfter="%"
                      value={restValuePercentage}
                      onChange={(val) => {
                        setFieldsValue({ input: { wishedRestValue: calculatePercentageRestValue(val, vehiclePrice) } })
                        setRestValuePercentage(val)
                      }}
                      style={!restValueInLimit ? { borderColor: "red", color: "red", width: 96 } : { width: 96 }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
          </>
        );
      }}
    </Form.Item>
  );
};

export default LeasingLimitsFields;

const calculateRestValuePercentage = (restValue: number, vehiclePrice: number) => {
  const p = 1 - (restValue / vehiclePrice)
  return isNaN(p) ? null : Math.round(p * 100)
}
const calculatePercentage = (part: number | null, whole: number | null) => {
  let p = (part || 0) / (whole || 0)
  if (isNaN(p)) p = 0;
  return Math.round(p * 100)
}
const calculatePercentageRestValue = (percentage: number | null, vehiclePrice: number | null): number => {
  const p = (percentage ?? 0) / 100;
  const res = (vehiclePrice ?? 0) * (1 - p);
  return isNaN(res) ? (vehiclePrice ?? 0) : Math.round(res);
}
const calculatePercentagePayout = (percentage: number | null, totalCost: number | null): number | undefined => {
  const p = (percentage ?? 0) / 100;
  const res = (totalCost ?? 0) * p;
  return isNaN(res) ? undefined : Math.round(res);
}

// NOTE: The purpose of this component is to get rid of a Bad setState warning
// Warning: Cannot update a component (LeasingDealExtraFields) while rendering a different component (Field). To locate the bad setState() call inside Field, follow the stack trace as described in https://fb.me/setstate-in-render
// setState was being called inside of a render block
interface InLimitStatusSyncProps {
  inLimit: boolean;
  inLimitStatus: boolean;
  onChange: (value: boolean) => void;
}

const InLimitStatusSync: FC<InLimitStatusSyncProps> = ({
  inLimit,
  inLimitStatus,
  onChange,
}) => {
  useEffect(() => {
    if (inLimit !== inLimitStatus) {
      onChange(inLimit);
    }
  }, [inLimit, inLimitStatus, onChange]);

  return null;
};

